import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  AddOnRateType,
  AddOnType,
  deleteV1CompaniesCompanyIdAutoAddOnAssignmentsId,
  patchV1CompaniesCompanyIdAutoAddOnAssignmentsId,
  postV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RateCard } from '~components/Cards/RateCard/RateCard';
import { NoDataFound } from '~components/DataGrid/NoDataFound';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { ActiveItemsVirtualLazyLoadList } from '~components/Lists/ActiveItemsVirtualLazyLoadList';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { QuickFilterButtonGroup } from '~components/QuickFilterButtonGroup/QuickFilterButtonGroup';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { FormStateChangeProps } from '~formsShared/types';
import { Account } from '~hooks/useAccount/models';
import useAccount from '~hooks/useAccount/useAccount';
import useLatestRequestSignal from '~hooks/useLatestRequestSignal/useLatestRequestSignal';
import { Rate } from '~hooks/useRates/models';
import { useRates } from '~hooks/useRates/useRates';
import { User } from '~hooks/useUsers/models';
import { useUserRoles } from '~hooks/useUsers/useUserRoles';
import useUsers from '~hooks/useUsers/useUsers';
import LoaderTread from '~icons/LoaderTread';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { Paginated, Pagination } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { CopyRateDialog } from './CopyRateDialog';
import {
  CreateEditRateCardDialog,
  CreateEditRateCardDialogRef,
} from './CreateEditRateCardDialog';
import InternalDriversList from './InternalDriversList';
import { DEFAULT_COMPANY_RATE_FAKE_ID } from './rateUtils';

interface CurrentSelectionState {
  type: RateOwnerType;
  id: string;
}

interface DriversListState {
  isLoading: boolean;
  list: User[];
}

const LEFT_COLUMN_WIDTH = '35%';
const RIGHT_COLUMN_WIDTH = '65%';
const INITIAL_ACCOUNTS_PAGINATION: Pagination = { limit: MAX_API_PAGE_SIZE, page: 1 };

const RatesManagement = observer(() => {
  const theme = useTheme();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [copyRateDialogState, setCopyRateDialogState] = useState<{
    isOpen: boolean;
    rate?: Rate;
  }>({ isOpen: false });
  const [currentSelection, setCurrentSelection] = useState<CurrentSelectionState>({
    type: RateOwnerType.VENDOR,
    id: DEFAULT_COMPANY_RATE_FAKE_ID,
  });
  const [selectedRateId, setSelectedRateId] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  // oxlint-disable-next-line @typescript-eslint/no-unused-vars
  const [isPrimaryDefined, setIsPrimaryDefined] = useState<boolean>(false);
  const [driversList, setDriversList] = useState<DriversListState>({
    isLoading: true,
    list: [],
  });
  const [deleteDialogState, setDeleteDialogState] = useState<{
    isDeleting: boolean;
    isOpen: boolean;
  }>({ isDeleting: false, isOpen: false });

  const { userStore, toasterStore, companyAssetsStore } = useStores();
  const {
    getAccountRatesUsingOwnerType,
    deleteRate,
    updateRate,
    createRate,
    isLoadingRates,
    isUpdatingRate,
  } = useRates();
  const { getUserRoles } = useUserRoles();
  const { getAllCompanyAccounts } = useAccount();
  const { getFilteredCompanyUsers } = useUsers();

  const companyId = getEffectiveUserCompanyId(userStore);

  const rateItems = useMemo(() => {
    return companyAssetsStore.rates
      ? companyAssetsStore.rates?.slice().sort((a) => (a.isPrimary ? -1 : 1))
      : [];
  }, [companyAssetsStore.rates, currentSelection]);

  const selectedRate: Nullable<Rate> = useMemo(() => {
    return companyAssetsStore.rates.find((rate) => rate.id === selectedRateId) || null;
  }, [selectedRateId, rateItems]);

  const [accountItems, setAccountItems] = useState<Account[]>([]);
  const [accountsPagination, setAccountsPagination] = useState<Pagination>(
    INITIAL_ACCOUNTS_PAGINATION,
  );
  const [isLoading, setIsLoading] = useState(false);
  const issueAccountsRequestSignal = useLatestRequestSignal();

  const accountItemsFilteredByRateAccountType = useMemo(() => {
    return accountItems.filter((acc) => {
      return (
        (acc.accountTypes as string[]).includes(
          `${copyRateDialogState.rate?.ownerType}`,
        ) && acc.id !== currentSelection.id
      );
    });
  }, [accountItems, copyRateDialogState.rate]);

  const buttons = [
    {
      title: t('administration.rates.vendor'),
      value: RateOwnerType.VENDOR,
      action: () => {
        setCurrentSelection({
          id: DEFAULT_COMPANY_RATE_FAKE_ID,
          type: RateOwnerType.VENDOR,
        });
      },
    },
    {
      title: t('administration.rates.customer'),
      value: RateOwnerType.CUSTOMER,
      action: () => {
        setCurrentSelection({
          id: DEFAULT_COMPANY_RATE_FAKE_ID,
          type: RateOwnerType.CUSTOMER,
        });
      },
    },
    {
      title: t('administration.rates.internal_drivers'),
      value: RateOwnerType.DRIVER,
      action: () => {
        setCurrentSelection({
          id: DEFAULT_COMPANY_RATE_FAKE_ID,
          type: RateOwnerType.DRIVER,
        });
      },
    },
  ];

  const selectedAccount = useMemo(() => {
    return accountItems.find((account) => account.id === currentSelection.id) || null;
  }, [currentSelection.id, accountItems]);

  const selectedDriver = useMemo(() => {
    return driversList.list.find((driver) => driver.id === currentSelection.id) || null;
  }, [currentSelection.id, driversList.list]);

  const rateFormDialogTitle = useMemo(() => {
    if (currentSelection.id === DEFAULT_COMPANY_RATE_FAKE_ID) {
      return t('administration.rates.account_default');
    }

    if (currentSelection.type === RateOwnerType.DRIVER) {
      const driver = driversList.list.find((driver) => driver.id === currentSelection.id);

      return String(driver?.fullName);
    }

    return String(selectedAccount?.name);
  }, [selectedAccount?.name, currentSelection, driversList.list]);

  const userPermissions = userStore.getPermissions();

  useEffect(() => {
    if (companyId) {
      setDriversList({ isLoading: true, list: [] });

      void getFilteredCompanyUsers(companyId, { access: 'execute_jobs' })
        .then((response) => {
          setDriversList({ isLoading: false, list: response });
        })
        .catch(() => {
          // Error toast is handled in the hook.
          setDriversList({ isLoading: false, list: [] });
        });
    }
  }, [companyId]);

  useEffect(() => {
    getUserRoles();
  }, []);

  useEffect(() => {
    setAccountItems([]);
    setAccountsPagination(INITIAL_ACCOUNTS_PAGINATION);
    setIsLoading(false);

    if (currentSelection.type === RateOwnerType.DRIVER) {
      issueAccountsRequestSignal();
      return;
    }

    const fetchAccounts = async () => {
      const signal = issueAccountsRequestSignal();

      try {
        const { data, pagination } = await getAllCompanyAccounts({
          companyId: companyId,
          accountTypes: [currentSelection.type],
          signal,
        });
        if (signal.aborted) return;

        setAccountItems(data);
        setAccountsPagination(pagination);
      } catch (error) {
        if (signal.aborted) return;

        reportRequestError(
          error,
          t('error_messages.account.failed_to_fetch_company_accounts'),
        );
      }
    };

    void fetchAccounts();
  }, [
    currentSelection.type,
    companyId,
    getAllCompanyAccounts,
    issueAccountsRequestSignal,
  ]);

  useEffect(() => {
    if (rateItems?.length) {
      setIsPrimaryDefined(rateItems.some((rate) => rate.isPrimary));
    } else {
      setIsPrimaryDefined(false);
    }
  }, [rateItems, selectedAccount]);

  useEffect(() => {
    if (currentSelection.id !== DEFAULT_COMPANY_RATE_FAKE_ID) {
      const loadRates = async () => {
        const params: Parameters<typeof getAccountRatesUsingOwnerType>[0] = {
          ownerType: currentSelection.type,
          companyId,
        };

        if (currentSelection.type === RateOwnerType.DRIVER) {
          params.driverId = currentSelection.id;
        } else {
          params.accountId = currentSelection.id;
        }

        const response = await getAccountRatesUsingOwnerType(params);

        if (response) {
          updateRatesInCompanyAssetsStore(response);
        }
      };

      void loadRates();
    }
  }, [currentSelection]);

  useEffect(() => {
    companyAssetsStore.setRates([]);
    setSelectedRateId(DEFAULT_COMPANY_RATE_FAKE_ID);
    getCompanyDefaultRate();
  }, [currentSelection.type]);

  const createEditRateDialogRef = useRef<CreateEditRateCardDialogRef>(null);

  const loadMoreAccounts = useCallback(async () => {
    if (isLoading) return;
    setIsLoading(true);
    const signal = issueAccountsRequestSignal();

    try {
      const { data, pagination } = await getAllCompanyAccounts({
        companyId: companyId,
        accountTypes: [currentSelection.type],
        cursor: accountsPagination.after,
        link: 'after',
        signal,
      });
      if (signal.aborted) return;

      setAccountItems((prevItems) => [...prevItems, ...data]);
      setAccountsPagination(pagination);
    } catch (error) {
      if (signal.aborted) return;

      reportRequestError(
        error,
        t('error_messages.account.failed_to_fetch_company_accounts'),
      );
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [
    isLoading,
    currentSelection.type,
    companyId,
    accountsPagination.after,
    getAllCompanyAccounts,
    issueAccountsRequestSignal,
  ]);

  const handleClose = () => {
    setDialogOpen(false);
  };

  const editRate = (id: string) => {
    setSelectedRateId(id);
    setDialogOpen(true);
  };

  const createNew = () => {
    setSelectedRateId(null);
    setDialogOpen(true);
  };

  const deleteRateHandler = (id: string) => {
    setSelectedRateId(id);
    setDeleteDialogState({ isOpen: true, isDeleting: false });
  };

  const handleDeleteRate = useCallback(() => {
    if (selectedRateId) {
      setDeleteDialogState((state) => ({ ...state, isDeleting: true }));

      void (async () => {
        try {
          await deleteRate({ id: selectedRateId });
          setDeleteDialogState({ isOpen: false, isDeleting: false });

          toasterStore.push(
            alert(t('administration.rates.rate_deleted'), AlertTypes.success),
          );
        } catch (error) {
          console.error('Failed to delete rate:', error);
          setDeleteDialogState((state) => ({ ...state, isDeleting: false }));
        }
      })();
    }
  }, [selectedRateId]);

  const onSuccess = (rate?: Rate) => {
    handleClose();
    const name = `${rate?.name}`;
    toasterStore.push(
      alert(
        selectedRateId
          ? t('administration.rates.rate_updated', { name })
          : t('administration.rates.rate_created', { name }),
        AlertTypes.success,
      ),
    );
  };
  const onSubmitCallback = (data: any) => {
    const rateCompanyId: string = data.company?.id || companyId;

    void (async () => {
      try {
        if (selectedRateId) {
          const updatedRate = await updateRate({
            companyId: rateCompanyId,
            rate: data,
            ownerType: currentSelection.type,
          });

          // Three sequential phases (DELETE → PATCH → POST). Strict
          // ordering means a cascade-replacement POST carrying
          // `original_auto_add_on_assignment_id` can't race the DELETE of
          // the row it references on the wire, and a POST that depends on
          // an updated parent state sees the post-PATCH state. Each
          // phase's `.map` must be evaluated *after* the prior phase's
          // `await` resolves — the SDK fires requests eagerly when the
          // function is invoked.
          const deleteResults = await Promise.allSettled(
            data.automaticAddOns
              .filter(
                (autoAddOn: AddOnAssignmentSchema) =>
                  autoAddOn.isDeleted && !autoAddOn.isNew,
              )
              .map((autoAddOn: AddOnAssignmentSchema) =>
                deleteV1CompaniesCompanyIdAutoAddOnAssignmentsId({
                  path: { 'company-id': rateCompanyId, id: autoAddOn?.addOn?.id ?? '' },
                }),
              ),
          );

          const patchResults = await Promise.allSettled(
            data.automaticAddOns
              .filter(
                (autoAddOn: AddOnAssignmentSchema) =>
                  !autoAddOn.isDeleted && !autoAddOn.isNew,
              )
              .map((autoAddOn: AddOnAssignmentSchema) =>
                patchV1CompaniesCompanyIdAutoAddOnAssignmentsId({
                  path: { 'company-id': rateCompanyId, id: autoAddOn?.addOn?.id ?? '' },
                  body: {
                    name: autoAddOn?.addOn?.name ?? '',
                    quantity: autoAddOn?.addOn?.quantity ?? 0,
                    revenue_rate_value:
                      autoAddOn?.addOn?.rateType === AddOnRateType.RATE_FOR_EACH ||
                      autoAddOn?.addOn?.rateType === AddOnRateType.PER_LOAD
                        ? autoAddOn?.addOn?.rateValue?.toString()
                        : undefined,
                    percentage:
                      autoAddOn?.addOn?.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                        ? autoAddOn?.addOn?.rateValue
                        : undefined,
                    add_on_rate_type: autoAddOn?.addOn?.rateType as AddOnRateType,
                    add_on_types: (autoAddOn?.addOn?.addOnTypes?.map(
                      (addOnType: { id: string; name: string }) => addOnType.id,
                    ) ?? []) as AddOnType[],
                    cost_rate_value: autoAddOn?.addOn?.costRateValue
                      ? String(autoAddOn?.addOn?.costRateValue)
                      : undefined,
                    total_installments: autoAddOn?.addOn?.totalInstallments,
                  },
                }),
              ),
          );

          const postResults = await Promise.allSettled(
            data.automaticAddOns
              .filter(
                (autoAddOn: AddOnAssignmentSchema) =>
                  !autoAddOn.isDeleted && autoAddOn.isNew,
              )
              .map((autoAddOn: AddOnAssignmentSchema) =>
                postV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments({
                  path: { 'company-id': rateCompanyId, 'rate-id': selectedRateId },
                  body: {
                    name: autoAddOn?.addOn?.name ?? '',
                    quantity: autoAddOn?.addOn?.quantity ?? 0,
                    revenue_rate_value:
                      autoAddOn?.addOn?.rateType === AddOnRateType.RATE_FOR_EACH ||
                      autoAddOn?.addOn?.rateType === AddOnRateType.PER_LOAD
                        ? autoAddOn?.addOn?.rateValue
                        : undefined,
                    percentage:
                      autoAddOn?.addOn?.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                        ? autoAddOn?.addOn?.rateValue
                        : undefined,
                    add_on_rate_type: autoAddOn?.addOn?.rateType as AddOnRateType,
                    external_id: autoAddOn?.addOn?.externalId ?? '',
                    accounting_id: autoAddOn?.addOn?.accountingId ?? '',
                    add_on_id: autoAddOn?.addOn?.addOnId ?? '',
                    add_on_types: (autoAddOn?.addOn?.addOnTypes?.map(
                      (addOnType: { id: string; name: string }) => addOnType.id,
                    ) ?? []) as AddOnType[],
                    total_installments: autoAddOn?.addOn?.totalInstallments ?? 0,
                    cost_rate_value: autoAddOn?.addOn?.costRateValue ?? undefined,
                  },
                }),
              ),
          );

          // Surface the first rejection so callers' surrounding error
          // handling sees the failure. Without this rethrow, partial
          // failures would be silently swallowed.
          const addOnFirstRejection = [
            ...deleteResults,
            ...patchResults,
            ...postResults,
          ].find((r): r is PromiseRejectedResult => r.status === 'rejected');
          if (addOnFirstRejection) {
            throw addOnFirstRejection.reason;
          }

          onSuccess(updatedRate);
        } else {
          const createdRate = await createRate({
            companyId: rateCompanyId,
            rate: data,
            ownerType: currentSelection.type,
          });

          await Promise.all([
            ...data.automaticAddOns
              .filter(
                (autoAddOn: AddOnAssignmentSchema) =>
                  !autoAddOn.isDeleted && autoAddOn.isNew,
              )
              .map((autoAddOn: AddOnAssignmentSchema) =>
                postV1CompaniesCompanyIdRatesRateIdAutoAddOnAssignments({
                  path: {
                    'company-id': rateCompanyId,
                    'rate-id': createdRate?.id ?? '',
                  },
                  body: {
                    name: autoAddOn?.addOn?.name ?? '',
                    quantity: autoAddOn?.addOn?.quantity ?? 0,
                    revenue_rate_value:
                      autoAddOn?.addOn?.rateType === AddOnRateType.RATE_FOR_EACH ||
                      autoAddOn?.addOn?.rateType === AddOnRateType.PER_LOAD
                        ? autoAddOn?.addOn?.rateValue
                        : undefined,
                    percentage:
                      autoAddOn?.addOn?.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                        ? autoAddOn?.addOn?.rateValue
                        : undefined,
                    add_on_rate_type: autoAddOn?.addOn?.rateType as AddOnRateType,
                    external_id: autoAddOn?.addOn?.externalId ?? '',
                    accounting_id: autoAddOn?.addOn?.accountingId ?? '',
                    add_on_id: autoAddOn?.addOn?.addOnId ?? '',
                    add_on_types: (autoAddOn?.addOn?.addOnTypes?.map(
                      (addOnType: { id: string; name: string }) => addOnType.id,
                    ) ?? []) as AddOnType[],
                    total_installments: autoAddOn?.addOn?.totalInstallments ?? 0,
                    cost_rate_value: autoAddOn?.addOn?.costRateValue ?? undefined,
                  },
                }),
              ),
          ]);

          onSuccess(createdRate);
        }
      } catch (error) {
        console.error('Failed to save rate:', error);
      }
    })();
  };

  const onSubmitForm = () => {
    createEditRateDialogRef.current?.submit(onSubmitCallback);
  };

  const updateRatesInCompanyAssetsStore = ({ data, pagination }: Paginated<Rate>) => {
    companyAssetsStore.setRates(data);
    companyAssetsStore.setRatesPagination(pagination);
  };
  const getCompanyDefaultRate = async () => {
    setCurrentSelection({ ...currentSelection, id: DEFAULT_COMPANY_RATE_FAKE_ID });

    const response = await getAccountRatesUsingOwnerType({
      ownerType: currentSelection.type,
      companyId,
    });

    if (response) {
      updateRatesInCompanyAssetsStore(response);
    }
  };
  const onFormStateChange = ({ isDirty }: FormStateChangeProps) => {
    setIsDirty(isDirty);
  };

  return (
    <>
      {userPermissions.canEditCompany && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'start',
            }}
          >
            <SettingsPageHeader
              title={t('settings.nav_items.freight_rates')}
              description={t('settings.page_descriptions.freight_rates')}
            />
          </Box>
        </Box>
      )}
      <Box
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          mt: 2,
        })}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            alignItems: 'center',
            minHeight: '82px',
          }}
        >
          <Box
            sx={{
              width: LEFT_COLUMN_WIDTH,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <QuickFilterButtonGroup
              buttons={buttons}
              color={theme.palette.action.selected}
              currentActiveFilter={currentSelection.type}
            />
          </Box>
          <Divider orientation={'vertical'} variant={'fullWidth'} flexItem></Divider>
          <Box
            sx={{
              width: RIGHT_COLUMN_WIDTH,
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'end',
            }}
          >
            {userPermissions?.canCreateRate && (
              <SmallButton color="brandV2Yellow" sx={{ mr: 2 }} onClick={createNew}>
                {t('actions.create_rate_card')}
              </SmallButton>
            )}
          </Box>
        </Box>
        <Divider variant={'fullWidth'} flexItem></Divider>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: LEFT_COLUMN_WIDTH,
              borderRight: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                width: '100%',
                overflow: 'hidden',
                // +25px over the prior 220px offset accounts for the taller
                // SettingsPageHeader (added subtitle) above the rates panel.
                height: `calc(100vh - 245px - var(--tread-agent-dock-height, 0px))`,
              }}
            >
              <Box>
                {/*Keep, for default company rate we have to mock it as account item, that fetches rates without accountId*/}
                <ListItemButton
                  selected={currentSelection.id === DEFAULT_COMPANY_RATE_FAKE_ID}
                  onClick={getCompanyDefaultRate}
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    borderRight:
                      currentSelection.id === DEFAULT_COMPANY_RATE_FAKE_ID
                        ? `2px solid ${theme.palette.primary.main}`
                        : '',
                  }}
                >
                  <ListItemText
                    sx={{ fontWeight: 600 }}
                    primary={t('administration.rates.default_rate')}
                  />
                </ListItemButton>
              </Box>

              {currentSelection.type === RateOwnerType.DRIVER ? (
                <InternalDriversList
                  drivers={driversList.list}
                  isLoading={driversList.isLoading}
                  selectedDriverId={currentSelection.id}
                  onSelectedDriverChange={(driverId: string) => {
                    setCurrentSelection({ ...currentSelection, id: driverId });
                  }}
                />
              ) : (
                <ActiveItemsVirtualLazyLoadList
                  titleProperty={'name'}
                  hasNextPage={Boolean(accountsPagination.after?.length)}
                  items={accountItems}
                  loadNextPage={loadMoreAccounts}
                  activeItemId={selectedAccount?.id || ''}
                  itemHeight={48}
                  onClickCallBack={(data) => {
                    setCurrentSelection({ ...currentSelection, id: data.id });
                  }}
                />
              )}
            </Box>
          </Box>
          <Box
            sx={{
              width: RIGHT_COLUMN_WIDTH,
              display: 'flex',
              flexDirection: 'column',
              alignItems: rateItems?.length ? 'center' : '',
              // Matches the left panel: +25px for the taller SettingsPageHeader.
              maxHeight: 'calc(100vh - 245px - var(--tread-agent-dock-height, 0px))',
              overflowY: 'auto',
              pt: 2,
            }}
          >
            {isLoadingRates && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  width: '100%',
                }}
              >
                <LoaderTread />
              </Box>
            )}
            {!isLoadingRates && !rateItems?.length && (
              <NoDataFound noDataText={`${t('administration.rates.empty_state_text')}`} />
            )}

            {!isLoadingRates &&
              Boolean(rateItems?.length) &&
              rateItems.map((rate) => {
                return (
                  <Box key={rate.id} sx={{ display: 'inline-flex', ml: 2, mb: 2 }}>
                    <RateCard
                      rate={rate}
                      onEdit={editRate}
                      deleteClickCallBack={deleteRateHandler}
                      copyClickCallBack={
                        currentSelection.type === RateOwnerType.DRIVER
                          ? undefined
                          : () => setCopyRateDialogState({ isOpen: true, rate })
                      }
                    />
                  </Box>
                );
              })}
          </Box>
        </Box>
      </Box>
      <CreateEditRateCardDialog
        ref={createEditRateDialogRef}
        isOpen={isDialogOpen}
        onClose={handleClose}
        selectedRate={selectedRate}
        selectedRateId={selectedRateId}
        isUpdatingRate={isUpdatingRate}
        isDirty={isDirty}
        onFormStateChange={onFormStateChange}
        onSubmit={onSubmitForm}
        rateFormDialogTitle={rateFormDialogTitle}
        defaultRateOwnerType={currentSelection.type}
        selectedAccount={selectedAccount}
        selectedDriver={selectedDriver}
      />
      <CopyRateDialog
        accountOptions={accountItemsFilteredByRateAccountType}
        isOpen={copyRateDialogState.isOpen}
        onClose={() => setCopyRateDialogState({ isOpen: false })}
        rate={copyRateDialogState.rate}
      />
      <ConfirmationDialog
        isBusy={deleteDialogState.isDeleting}
        isOpen={deleteDialogState.isOpen}
        onClose={() => setDeleteDialogState({ isOpen: false, isDeleting: false })}
        onConfirm={handleDeleteRate}
        title={t('administration.rates.delete_rate_title')}
      >
        <Typography>{t('administration.rates.delete_rate_text')}</Typography>
      </ConfirmationDialog>
    </>
  );
});

export { RatesManagement };
